import { useForm, bindField } from "@npco/zeller-design-system/form";
import * as Icon from "@npco/zeller-design-system/icon";
import * as Input from "@npco/zeller-design-system/input";
import type { ADT } from "pb.adt";
import { useMemo, useState, useEffect, useCallback } from "react";
import { z } from "zod";

import {
	CustomerRole,
	OnboardingStatus,
} from "../../../../schema.dev-sdk-api-appsync.generated";
import { Layout } from "../shared/layout";
import { useApiClient } from "../shared/use-api-client";
import type { Page } from "../terminal-setup";
import { getEntities } from "./get-entities";

export function PageEntity({
	page: { onNext },
	onClose,
	onLogout,
	pending,
}: {
	page: ADT.Pick<Page, "Entity">;
} & Required<Pick<Layout.Props, "onClose" | "onLogout" | "pending">>) {
	const form = useForm(ValuesSchema, ({ value }) => {
		onNext(value.entityId);
	});

	const entities = useInputTilesEntitiesPreset();

	return (
		<form.Subscribe selector={(state) => state.isSubmitting}>
			{(isSubmitting) => (
				<Layout
					progress
					progressValue={1}
					progressLimit={4}
					title="Choose Business"
					onClose={onClose}
					onLogout={onLogout}
					actions={[
						{ label: "Next", onClick: form.handleSubmit, primary: true },
					]}
					pending={pending || entities.pending || (isSubmitting && "submit")}
				>
					<div className="px-32 pt-6">
						<form.Field
							name="entityId"
							children={(field) => (
								<Input.Tiles
									{...bindField(field)}
									{...entities}
									label="Find your business"
								/>
							)}
						/>
					</div>
				</Layout>
			)}
		</form.Subscribe>
	);
}

const ValuesSchema = z.object({
	entityId: z.string(),
});

// ============================================================================

function useInputTilesEntitiesPreset() {
	const [optionsData, setOptionsData] = useState<
		ReadonlyArray<Input.Tiles.Option> | Error
	>();

	const apiClient = useApiClient();

	const retry = useCallback(async () => {
		setOptionsData(undefined);

		const response = await getEntities({ apiClient });
		if (response instanceof Error) {
			setOptionsData(response);
			return;
		}

		return setOptionsData(
			response.entityRelations?.map(
				({
					role,
					entityUuid,
					entity: { name, tradingName, onboardingStatus },
				}) => {
					const isAdmin = role === CustomerRole.ADMIN;
					const isNotOnboarded =
						onboardingStatus !== OnboardingStatus.ONBOARDED &&
						onboardingStatus !== OnboardingStatus.RC_ONBOARDED;
					return {
						id: entityUuid,
						label: tradingName || name || "",
						icon: <Icon.Buildings />,
						tag: isAdmin
							? { label: "Admin", color: "blue" }
							: { label: "Manager" },
						badge: isNotOnboarded
							? { label: "Not Onboarded", color: "red", outline: true }
							: undefined,
					};
				},
			),
		);
	}, [apiClient]);

	useEffect(() => {
		retry();
	}, [retry]);

	const [searchValue, setSearchValue] = useState<string>();
	const options = useMemo(() => {
		if (optionsData instanceof Error) {
			return undefined;
		}

		if (!searchValue) {
			return optionsData;
		}

		const searchValueCompare = searchValue.toLowerCase();
		return optionsData?.filter((option) => {
			const optionLabelCompare = option.label.toLowerCase();
			return optionLabelCompare.includes(searchValueCompare);
		});
	}, [optionsData, searchValue]);

	return {
		pending: optionsData === undefined,
		options,
		optionsError: optionsData instanceof Error ? "network" : undefined,
		optionsOnRetry: retry,
		search: true,
		searchValue,
		searchOnChange: setSearchValue,
		searchPlaceholder: "Find your business",
	} satisfies Partial<Input.Tiles.Props>;
}
